/**
 * Marcações que existem só na TELA do editor e não fazem parte do texto.
 *
 * Espelho de `App\Support\MarcacoesDeTela` no backend — as duas pontas precisam
 * concordar sobre o que é "de tela", senão a correção que o editor acha o
 * servidor não acha (e vice-versa).
 *
 * - `id="sec-<nível>-<posição>"`: o EditorView numera os títulos pela posição
 *   ao abrir o documento, para a navegação pela estrutura. O número muda de
 *   dono quando um título entra ou sai, e um título que vira parágrafo leva o
 *   número junto. Não identifica bloco nenhum fora daquela abertura — e, salvo
 *   no texto, fazia a correção de um título ser aplicada em outro (trabalho 234,
 *   09/10/2026). Os ids de título vindos do Word ou do usuário têm outro formato.
 * - classes `mce-*` (ex.: `mce-item-anchor` na âncora `<a name="_Hlk…">` do
 *   Word): o TinyMCE põe na tela e tira ao salvar, mas o `outerHTML` que a
 *   correção captura as leva junto.
 */

const ID_PROVISORIO = /^sec-\d+-\d+(?:-\d+)?$/;

/** Mesmo padrão do backend: só um atributo de verdade, nunca texto corrido. */
const ATRIBUTO_ID_PROVISORIO =
  /(<[a-zA-Z][a-zA-Z0-9]*\b[^<>]*?)\s+id\s*=\s*(["'])sec-\d+-\d+(?:-\d+)?\2/g;

export function ehIdProvisorioDeTitulo(id: string | null | undefined): boolean {
  return !!id && ID_PROVISORIO.test(id.trim());
}

export function ehClasseDeTela(classe: string): boolean {
  return classe.startsWith("mce-");
}

/** Os ids que ainda identificam um bloco depois de reabrir o documento. */
export function semIdsProvisorios(ids: string[] | null | undefined): string[] {
  return (ids ?? []).filter((id) => !ehIdProvisorioDeTitulo(id));
}

/**
 * Tira os ids provisórios do texto do trabalho que vai para o servidor.
 *
 * Por texto, e não pelo DOM, para não reescrever o resto do documento. Dentro
 * de outro atributo as aspas estão escapadas (`&quot;`), e no texto corrido o
 * `<` também — então só casa um atributo `id` de verdade.
 */
export function removerIdsProvisoriosDoConteudo(html: string): string {
  if (!html || !html.includes("sec-")) return html;
  return html.replace(ATRIBUTO_ID_PROVISORIO, "$1");
}

function limparElemento(elemento: Element): boolean {
  let mudou = false;

  if (ehIdProvisorioDeTitulo(elemento.getAttribute("id"))) {
    elemento.removeAttribute("id");
    mudou = true;
  }

  const classes = Array.from(elemento.classList);
  const restantes = classes.filter((classe) => !ehClasseDeTela(classe));
  if (restantes.length !== classes.length) {
    if (restantes.length > 0) elemento.setAttribute("class", restantes.join(" "));
    else elemento.removeAttribute("class");
    mudou = true;
  }

  // O texto de antes guardado no próprio bloco também volta para o texto ao
  // recusar.
  const original = elemento.getAttribute("data-original-html");
  if (original) {
    const limpo = removerMarcacoesDeTela(original);
    if (limpo !== original) {
      elemento.setAttribute("data-original-html", limpo);
      mudou = true;
    }
  }

  return mudou;
}

/**
 * Tira do HTML de uma correção o id provisório de título e as classes `mce-*`.
 * Devolve o mesmo texto, sem passar pelo DOM, quando não há o que tirar.
 */
export function removerMarcacoesDeTela(html: string): string {
  if (!html || (!html.includes("sec-") && !html.includes("mce-"))) return html;

  const template = document.createElement("template");
  template.innerHTML = html;
  let mudou = false;
  for (const elemento of Array.from(template.content.querySelectorAll("*"))) {
    if (limparElemento(elemento)) mudou = true;
  }

  return mudou ? template.innerHTML : html;
}
